import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  Badge,
  Grid,
  TabNavigation,
  TabNavigationItem
} from '@walmart-web/livingdesign-components';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import InfiniteScroll from 'react-infinite-scroll-component';
import _cloneDeep from 'lodash/cloneDeep';

import styles from './styles.module.css';
import UnreadNotifications from './UnreadNotifications/index.tsx';
import AllNotifications from './AllNotifications/index.tsx';
import {
  getNotificationsCount,
  getNotificationsList,
  markReadNotfications,
  NotificationListItem
} from '../../../api/notifications.ts';
import { API_STATUS_CODE } from '../../../constants/common.js';
import { logger } from '../../../utils/logger.js';
import { NotificationItemType } from './NotificationsSection/NotificationItem/index.tsx';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import NotificationsError from './NotificationsError/index.tsx';
import {
  notificationsActions,
  notificationsSelectors
} from '../../../redux/slices/notifications.ts';

type NotificationTabType = 'unread' | 'all';

type Props = {
  mobileView?: boolean;
  onClose?: () => void;
};

export type NotificationsListState = {
  unread: NotificationItemType[];
  last7Days: NotificationItemType[];
  older: NotificationItemType[];
  hasMore: boolean;
  lastPage: number;
  count: number;
  currentTotalNotificationsCount: number;
};

const convertApiResponseToNotificationItem = (data: NotificationListItem) => {
  return {
    id: data.id,
    title: data.title,
    message: data.message,
    date: data.createdAt,
    isRead: data.readReceipt,
    webLink: data.webLink,
    type: data.type
  };
};

const initState = {
  unread: [],
  last7Days: [],
  older: [],
  hasMore: false,
  lastPage: -1,
  count: 0,
  currentTotalNotificationsCount: 0
};

const NotificationsPanelBody: React.FC<Props> = ({ mobileView, onClose }) => {
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notificationsList, setNotificationsList] =
    useState<NotificationsListState>(initState);
  const notificationsListRef = useRef<NotificationsListState>(initState);
  const [notificationType, setNotificationType] =
    useState<NotificationTabType>('unread');
  const dispatch = useAppDispatch();
  const unreadCount = useAppSelector(notificationsSelectors.selectCount);
  const { t } = useTranslation('profile');

  const handleTabChange = (type: NotificationTabType) => {
    logger.clickStream('notification_panel_tab_change', {
      panelistId: localStorage.getItem('panelistId') || '',
      tab: type
    });
    setNotificationType(type);
  };

  const fetchNotifications = (
    isInit: boolean = false,
    signal?: AbortSignal
  ) => {
    setIsError(false);
    setLoading(true);
    const { unread, last7Days, older, lastPage } = _cloneDeep(
      isInit ? initState : notificationsList
    );
    getNotificationsList(
      {
        page: lastPage + 1,
        is_read: notificationType !== 'unread'
      },
      signal
    ).then((res) => {
      if (res.status === API_STATUS_CODE.SUCCESS) {
        res.data.items.forEach((item) => {
          if (item.readReceipt) {
            if (dayjs(item.createdAt).isAfter(dayjs().subtract(7, 'days')))
              last7Days.push(convertApiResponseToNotificationItem(item));
            else older.push(convertApiResponseToNotificationItem(item));
          } else unread.push(convertApiResponseToNotificationItem(item));
        });
        const listData = {
          unread,
          last7Days,
          older,
          hasMore: res.data.next_page,
          count: res.data.total_items,
          lastPage: lastPage + 1,
          currentTotalNotificationsCount:
            unread.length + last7Days.length + older.length
        };
        setNotificationsList(listData);
        notificationsListRef.current = listData;
      } else {
        if (signal?.aborted) return;
        setIsError(true);
        logger.error('Error while getting notifications', (res as any).error);
      }
      setLoading(false);
    });
  };

  const markNotificationsAsRead = () => {
    const unreadNotificationIds = notificationsListRef.current.unread.map(
      (notif) => notif.id
    );
    if (unreadNotificationIds.length === 0) return;
    logger.info('Marking notifications as read', {
      panelistId: localStorage.getItem('panelistId') || '',
      unreadNotificationsCount: unreadNotificationIds.length.toString()
    });
    return markReadNotfications(unreadNotificationIds).then((res) => {
      if (
        ![API_STATUS_CODE.SUCCESS, API_STATUS_CODE.NO_CONTENT].includes(
          res.status
        )
      ) {
        logger.error(
          'Error while marking notifications as read',
          (res as any).error
        );
      }
    });
  };

  const fetchCount = () => {
    getNotificationsCount().then((res) => {
      if (res.status === API_STATUS_CODE.SUCCESS) {
        dispatch(notificationsActions.setCount(res.data.count));
      } else {
        logger.error(
          'Error while getting notifications count',
          (res as any).error
        );
      }
    });
  };

  useEffect(() => {
    const controller = new AbortController();
    notificationsListRef.current = notificationsList;
    setNotificationsList(initState);
    fetchNotifications(true, controller.signal);

    return () => {
      controller.abort();
    };
  }, [notificationType]);

  useEffect(() => {
    fetchCount();

    return () => {
      const unreadNotificationsCount =
        notificationsListRef.current.unread.length;
      const updatedUnreadCount = unreadCount - unreadNotificationsCount;
      dispatch(notificationsActions.setCount(updatedUnreadCount));
      markNotificationsAsRead()?.then(() => {
        fetchCount();
      });
    };
  }, []);

  if (isError)
    return (
      <Grid style={{ width: '100%' }}>
        <NotificationsError onClick={onClose} />
      </Grid>
    );

  return (
    <Grid style={{ width: '100%' }}>
      <TabNavigation UNSAFE_className={styles.tabContainer}>
        <TabNavigationItem
          placeholder={t('notifications.unread')}
          onClick={() => handleTabChange('unread')}
          isCurrent={notificationType === 'unread'}
          UNSAFE_className={clsx(
            notificationType === 'unread' && styles.selectedTab
          )}
        >
          {t('notifications.unread')}
          <Badge UNSAFE_className={styles.unreadCountBadge}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        </TabNavigationItem>
        <TabNavigationItem
          placeholder={t('notifications.all')}
          onClick={() => handleTabChange('all')}
          isCurrent={notificationType === 'all'}
          UNSAFE_className={clsx(
            notificationType === 'all' && styles.selectedTab
          )}
        >
          {t('notifications.all')}
        </TabNavigationItem>
      </TabNavigation>

      <Grid
        id="notificationsScrollContainer"
        UNSAFE_className={clsx(styles.notificationsSectionsContainer, {
          [styles.mobileContainer]: mobileView
        })}
      >
        <InfiniteScroll
          dataLength={notificationsList.currentTotalNotificationsCount}
          next={fetchNotifications}
          hasMore={notificationsList.hasMore}
          loader={<></>}
          scrollableTarget="notificationsScrollContainer"
        >
          {notificationType === 'unread' ? (
            <UnreadNotifications loading={loading} data={notificationsList} />
          ) : (
            <AllNotifications loading={loading} data={notificationsList} />
          )}
        </InfiniteScroll>
      </Grid>
    </Grid>
  );
};

export default NotificationsPanelBody;
